/**
 * Programmatic mirror of the CSS tokens in `src/styles/tokens.css`.
 *
 * Only values that code genuinely has to compute with live here — avatar fills
 * picked by hash, SVG paint that cannot read a CSS variable, chart series
 * colours. Everything a class name can express should use the class name; a
 * unit test asserts the two files agree.
 */

export const brand = {
  ngahere: '#1F5C3D',
  ngaherTint: '#EAF3EE',
  pohutukawa: '#FF6B4A',
  pohutukawaHover: '#F0593A',
  moana: '#2BA6A4',
  moanaHover: '#1F8482',
  moanaDeep: '#0F4D4B',
  pounamu: '#3E7C5A',
  sand: '#F6F3EC',
  ink: '#1A1F1C',
} as const;

/**
 * The six avatar fills the design export assigns to members, in export order.
 * `avatarColour` picks deterministically so a person keeps their colour across
 * screens without the server having to store one.
 */
export const avatarPalette = [
  '#3E7C5A', // pounamu
  '#8A6FB0', // violet
  '#2BA6A4', // moana
  '#B0568F', // wahine
  '#C77B4A', // clay
  '#1F5C3D', // ngahere
] as const;

export type AvatarColour = (typeof avatarPalette)[number];

/** Deterministic avatar fill for a stable key (user id, or initials as a fallback). */
export function avatarColour(key: string): AvatarColour {
  let hash = 0;
  for (let i = 0; i < key.length; i += 1) {
    hash = (hash * 31 + key.charCodeAt(i)) >>> 0;
  }
  return avatarPalette[hash % avatarPalette.length] as AvatarColour;
}

/** Flat cartographic fills for the schematic map. */
export const mapPaint = {
  land: '#E9E8E3',
  water: '#D7E3E6',
  park: '#DCE6D8',
  road: '#FFFFFF',
  roadMinor: '#F4F2EC',
  grid: 'rgba(0,0,0,0.05)',
  routeCasing: '#FFFFFF',
  routeLine: '#FF6B4A',
  originRing: '#1F5C3D',
  destinationFill: '#FF6B4A',
} as const;

/** Series colours for the eco dashboard's monthly bars. */
export const chartPaint = {
  bar: '#9AC2AC',
  barPeak: '#1F5C3D',
} as const;

/** Status pill palettes, keyed by the states the product models. */
export const statusPaint = {
  success: { fg: '#2E7D32', bg: '#E7F2E8' },
  warning: { fg: '#D98E00', bg: '#FBF1DC' },
  danger: { fg: '#C8372D', bg: '#FDEDE8' },
  info: { fg: '#1F7A78', bg: '#EAF7F6' },
  neutral: { fg: '#5C665F', bg: '#F1EFE7' },
} as const;

export type StatusTone = keyof typeof statusPaint;
